import React, { useState, useEffect } from 'react';
import { Star, Mail, Phone, Building2, Briefcase, Factory, Pencil, Loader2 } from 'lucide-react';
import { useLanguage } from '../../i18n/index.jsx';
import { contactInitials } from '../../utils/avatar';
import contactService from '../../services/contactService';
import InteractionBar from './InteractionBar.jsx';
import { userErrorMessage } from '../../utils/userError';

// 44px initials avatar — PAI handoff §7: a 10%-white disc with a 16%-white
// hairline and 16px/700 initials. (Supersedes the white/black ball of New
// Design #72 on THIS surface only; the board/rail avatars are unchanged.)
const InitialsBall = ({ name }) => (
    <div
        data-testid="contact-ball"
        className="w-11 h-11 rounded-full bg-white/[0.1] border border-white/[0.16] text-white flex items-center justify-center text-[16px] font-bold flex-shrink-0"
    >
        {contactInitials(name)}
    </div>
);

// Profile block shown at the top of an expanded row (Section 1, FE-3).
const ProfileData = ({ contact }) => {
    const email = contact.email || (contact.emails && contact.emails[0]) || '';
    const phone = contact.phone || (contact.phones && contact.phones[0]) || '';
    const rows = [];
    if (email) rows.push({ Icon: Mail, value: email, href: `mailto:${email}` });
    if (phone) rows.push({ Icon: Phone, value: phone, href: `tel:${phone}` });
    if (contact.company) rows.push({ Icon: Building2, value: contact.company });
    if (contact.position) rows.push({ Icon: Briefcase, value: contact.position });
    if (contact.industry) rows.push({ Icon: Factory, value: contact.industry, testId: 'contact-profile-industry' });
    return (
        <section data-testid="contact-profile-data" className="bg-white/[0.03] rounded-row border border-pai-subtle p-3.5 space-y-2">
            {rows.length === 0 ? (
                <p className="text-xs text-pai-faint italic">—</p>
            ) : rows.map(({ Icon, value, href, testId }, i) => (
                <div key={i} data-testid={testId} className="flex items-center gap-2.5 text-sm text-pai-body">
                    <Icon size={14} className="text-pai-faint flex-shrink-0" />
                    {href ? (
                        <a href={href} className="hover:underline truncate">{value}</a>
                    ) : (
                        <span className="truncate">{value}</span>
                    )}
                </div>
            ))}
        </section>
    );
};

// Rolodex "Invite to Network" — runs ONE discovery on first expand and offers the
// handshake when the saved email belongs to a discoverable PAI user (FE-3).
const RolodexInvite = ({ contact }) => {
    const { t } = useLanguage();
    const email = contact.email || (contact.emails && contact.emails[0]) || '';
    const [state, setState] = useState(null); // discover state
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        if (!email) return undefined;
        setLoading(true);
        contactService.discover(email)
            .then((res) => { if (!cancelled) setState(res?.state || null); })
            .catch(() => { if (!cancelled) setState(null); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [email]);

    if (!email) return null;
    if (loading) return <Loader2 size={14} className="text-pai-faint animate-spin" />;

    const send = async () => {
        setSending(true);
        setError(null);
        try {
            await contactService.sendRequest(email);
            setSent(true);
            try { window.dispatchEvent(new CustomEvent('omnis:contact-changed')); } catch (_) { /* ignore */ }
        } catch (err) {
            setError(userErrorMessage(err, t));
        } finally {
            setSending(false);
        }
    };

    const showSent = sent || state === 'pending_sent';
    if (showSent) {
        return (
            <span data-testid="rolodex-request-sent" className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-pai-meta bg-white/[0.06] rounded-full">
                {t('contactRequestSent')}
            </span>
        );
    }
    if (state === 'found') {
        return (
            <div>
                <button
                    data-testid="rolodex-connect-btn"
                    onClick={send}
                    disabled={sending}
                    className="px-3 py-1.5 text-xs font-semibold text-pai-blue border border-pai-blue/40 bg-pai-blue/[0.1] hover:bg-pai-blue/[0.16] disabled:opacity-50 rounded-ctl transition-colors"
                >
                    {t('contactInviteToNetwork')}
                </button>
                {error && <div className="mt-1 text-[11px] text-pai-red">{error}</div>}
            </div>
        );
    }
    return null;
};

const ContactRow = ({ contact, expanded, onToggle, onToggleFavorite, onEdit, navigate, onPreviewDoc }) => {
    const { t } = useLanguage();
    const isNetwork = contact.tier === 'network';
    // The role pill (handoff §7) carries the position, so the sub-line drops it and
    // falls back through the same chain it always did.
    const role = contact.position || '';
    const subtitle = contact.company
        || contact.phone || (contact.phones && contact.phones[0])
        || contact.email || (contact.emails && contact.emails[0]) || '—';

    return (
        <div className="border-b border-pai-hairline last:border-b-0" data-contact-id={contact.id}>
            <div
                role="button"
                tabIndex={0}
                data-testid="agenda-contact-row"
                onClick={onToggle}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
                className={`w-full flex items-center gap-4 px-[22px] py-[15px] text-left cursor-pointer transition-colors duration-150 ${
                    expanded ? 'bg-white/[0.055]' : 'hover:bg-white/[0.045]'
                }`}
            >
                <InitialsBall name={contact.name} />
                <div className="flex-1 min-w-0">
                    <div className="text-[15.5px] font-semibold text-white truncate">
                        {contact.name}
                    </div>
                    <div className="text-[13px] text-pai-meta truncate tnum">
                        {subtitle}
                    </div>
                </div>
                {role && (
                    <span
                        data-testid="contact-role-pill"
                        /* Always rendered — never `hidden sm:`. The sub-line no longer
                           carries the position, so a viewport-keyed gate would drop it
                           from the row entirely on the mobile layout (#171), and a
                           viewport breakpoint is the wrong signal for a DOCKED panel
                           anyway (the #142/#231 container-query precedent). It shrinks
                           instead: max-w + truncate beside a min-w-0 name. */
                        className="inline-flex flex-shrink-0 max-w-[9rem] truncate px-[11px] py-[5px] rounded-full border border-white/[0.16] text-white/60 text-[12px] font-semibold"
                    >
                        {role}
                    </span>
                )}
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onToggleFavorite(contact); }}
                    className={`w-9 h-9 rounded-[9px] flex items-center justify-center flex-shrink-0 transition-colors duration-150 ${
                        contact.is_favorite
                            ? 'text-pai-amber hover:bg-white/[0.07]'
                            : 'text-white/40 hover:text-pai-amber hover:bg-white/[0.07]'
                    }`}
                    title={contact.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
                >
                    <Star size={16} fill={contact.is_favorite ? 'currentColor' : 'none'} />
                </button>
            </div>

            {expanded && (
                <div data-testid="contact-expanded" className="px-[22px] pb-4 pt-1 space-y-2.5 bg-white/[0.02]">
                    <div className="flex items-center justify-between gap-2">
                        {/* Rolodex rows get the invite affordance here; network rows leave it empty. */}
                        <div className="min-w-0">
                            {!isNetwork && <RolodexInvite contact={contact} />}
                        </div>
                        <button
                            data-testid="contact-edit-btn"
                            onClick={(e) => { e.stopPropagation(); onEdit(contact); }}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-pai-secondary bg-white/[0.03] border border-pai hover:text-white hover:bg-white/[0.07] rounded-ctl flex-shrink-0 transition-colors"
                            title={t('contactEditAction')}
                        >
                            <Pencil size={12} /> {t('contactEditAction')}
                        </button>
                    </div>

                    <ProfileData contact={contact} />

                    {isNetwork && (
                        <InteractionBar contact={contact} navigate={navigate} onPreviewDoc={onPreviewDoc} />
                    )}
                </div>
            )}
        </div>
    );
};

export default ContactRow;
